class AlgoliaRangeInput extends HTMLElement {
  constructor() {
    super();

    this.connectedCallback = this.connectedCallback.bind(this);
    this.createWidget = this.createWidget.bind(this);
    this.getRangeValues = this.getRangeValues.bind(this);
    this.getSelector = this.getSelector.bind(this);
    this.render = this.render.bind(this);
    this.renderContainer = this.renderContainer.bind(this);
    this.renderContent = this.renderContent.bind(this);
    this.renderWrapper = this.renderWrapper.bind(this);
  }

  /**
   * Initializes settings based on custom attributes when the element is connected to the DOM.
   * @returns {void}
   */
  connectedCallback() {
    this.settings = {
      currencyIsoCode: this.getAttribute('currency-iso-code'),
      from: this.getAttribute('from'),
      to: this.getAttribute('to'),
      maxPrice: this.getAttribute('max-price')
    }
  }

  /**
   * Creates an instance of the range input widget.
   * @param {Object} configuration - Configuration object for rendering.
   * @param {Object} instances - Instances object containing algolia instances.
   * @returns {Object} The created range input widget instance.
   */
  createWidget(configuration, instances) {
    const { connectRange } = instances.instantSearch.connectors;
    const connectedRangeInput = connectRange((renderOptions) => this.render(configuration, renderOptions));

    const attribute = configuration.name;
    const selector = `[class~='${this.getSelector(configuration)}']`;
    const container = document.querySelector(selector);

    const rangeInputWidget = connectedRangeInput({ container, attribute });
    return rangeInputWidget;
  }

  /**
   * Formats the minimum and maximum values of a range based on the given configuration.
   * @param {Object} range - Contains the min and max range values.
   * @param {Array} start - Contains the selected min and max range values.
   * @param {boolean} usesCommaDecimals - Indicates whether to use comma decimals (`,`) for formatting.
   * @returns {Object} Object with the formatted minimum and maximum values based on the configuration.
   */
  getRangeValues(range, start, usesCommaDecimals) {
    const [min, max] = start;

    const rangeValues = {
      minValue: range.min,
      maxValue: range.max
    };

    if (Number.isFinite(min)) {
      if (usesCommaDecimals) {
        rangeValues.minValue = min.toString().replace('.', '').replace(',', '.');
      } else {
        rangeValues.minValue = min.toString().replace(',', '');
      }
    }

    if (Number.isFinite(max)) {
      if (usesCommaDecimals) {
        rangeValues.maxValue = max.toString().replace('.', '').replace(',', '.');
      } else {
        rangeValues.maxValue = max.toString().replace(',', '');
      }
    }

    return rangeValues;
  }

  /**
   * Returns the generated selector for elements based on the current configuration.
   * @param {Object} configuration - Configuration object for rendering.
   * @returns {string} Generated selector.
   */
  getSelector(configuration) {
    const prefix = 'ais-range-input-';
    const suffix = configuration.isMobile ? '-mobile-container' : '-container';
    const selector = `${prefix}${configuration.container}${suffix}`;
    return selector;
  }

  /**
   * Renders the range input widget with the provided render options.
   * @param {Object} configuration - Configuration object for rendering.
   * @param {Object} renderOptions - Options for rendering the widget.
   * @returns {void}
   */
  render(configuration, renderOptions) {

    const { canRefine, range, refine, start, widgetParams } = renderOptions;
    const { container } = widgetParams;

    const form = document.createElement('form');

    const fieldElements = this.renderContent(range, start);
    fieldElements.forEach(element => form.appendChild(element));


    container.classList.toggle('hidden', !canRefine);
    const elements = this.renderWrapper(configuration, range);
    container.innerHTML = '';

    elements.forEach(element => container.appendChild(element));
    container.appendChild(form);

    /* This implementation comes from the algolia range input docs. */
    form.addEventListener('submit', event => {
      event.preventDefault();

      const rawMinInputValue = parseFloat(event.target.elements.min.value);
      const rawMaxInputValue = parseFloat(event.target.elements.max.value);

      refine([
        Number.isFinite(rawMinInputValue) ? rawMinInputValue : undefined,
        Number.isFinite(rawMaxInputValue) ? rawMaxInputValue : undefined,
      ]);
    });
  }

  /**
   * Renders the container for the range input widget.
   * @param {Object} configuration - Configuration object for rendering.
   * @param {Function} html - The HTML template function.
   * @returns {string} The rendered container.
   */
  renderContainer(configuration, html) {
    const selector = this.getSelector(configuration);
    const classNames = `${configuration.isMobile ? "mobile-facets__details" : ""} facets__disclosure-vertical js-filter`;

    return html`
      <details class="${classNames} ${selector}" data-index="${selector}" id="details-${selector}" open="true"></details>
    `;
  }

  /**
   * Renders the content.
   * @param {Object} range - Contains the min and max range values.
   * @param {Array} start - Contains the selected min and max range values.
   * @returns {string} The rendered content HTML.
   */
  renderContent(range, start) {
    const currenciesUsingCommaDecimals = ['ANG','ARS','BRL','BYN','BYR','CLF','CLP','COP','CRC','CZK','DKK','EUR','HRK','HUF','IDR','ISK','MZN','NOK','PLN','RON','RUB','SEK','TRY','UYU','VES','VND'];
    const usesCommaDecimals = currenciesUsingCommaDecimals.includes(this.settings.currencyIsoCode);

    const rangeValues = this.getRangeValues(range, start, usesCommaDecimals);

    const minField = document.createElement('div');
    field.classList.add('field');

    // Min input
    const inputMin = document.createElement('input');
    inputMin.classList.add('field__input');
    inputMin.id = 'min';
    inputMin.name = 'min';
    inputMin.value = rangeValues.minValue;
    inputMin.type = 'number';
    inputMin.placeholder = range.min;
    inputMin.min = range.min;
    inputMin.max = range.max;
    minField.appendChild(inputMin);
    const labelMin = document.createElement('label');
    labelMin.classList.add('field__label');
    labelMin.htmlFor = 'min';
    labelMin.textContent = this.settings.from;
    minField.appendChild(labelMin);

    const maxField = document.createElement('div');

    // Max input
    const inputMax = document.createElement('input');
    inputMax.classList.add('field__input');
    inputMax.id = 'max';
    inputMax.name = 'max';
    inputMax.value = rangeValues.maxValue;
    inputMax.type = 'number';
    inputMax.placeholder = range.max;
    inputMax.min = range.min;
    inputMax.max = range.max;
    maxField.appendChild(inputMax);
    const labelMax = document.createElement('label');
    labelMax.classList.add('field__label');
    labelMax.htmlFor = 'max';
    labelMax.textContent = this.settings.to;
    maxField.appendChild(labelMax);

    const submit = document.createElement('input');
    submit.type = 'submit';
    submit.hidden = true;

    return [minField, maxField, submit];
  }

  /**
   * Renders the wrapper.
   * @param {Object} configuration - Configuration object for rendering.
   * @param {Object} range - Contains the min and max range values.
   * @returns {array} The rendered wrapper HTML.
   */
  renderWrapper(configuration, range) {
    // <summary class="facets__summary caption-large focus-offset">
    //   <div>
    //     <span class="p-regular p-bold">${title}</span>
    //     <div class="icon--expand-collapse">${iconContent}</div>
    //   </div>
    // </summary>
    // <div class="facets__display-vertical">
    //   <div class="facets__header-vertical">
    //     <span class="facets__selected">${label}</span>
    //   </div>
    // </div>
    const title = configuration.title;
    const icon = document.querySelector('algolia-icon-expand-collapse');
    const iconContent = icon ? icon.render() : [];

    const label = this.settings.maxPrice.replace('max_price', range.max);

    const summary = document.createElement('summary');
    summary.classList.add('facets__summary', 'caption-large', 'focus-offset');
    
    const div = document.createElement('div');
    const span = document.createElement('span');
    span.classList.add('p-regular', 'p-bold');
    span.textContent = title;
    div.appendChild(span);
    const iconDiv = document.createElement('div');
    iconDiv.classList.add('icon--expand-collapse');
    iconContent.forEach(element => iconDiv.appendChild(element));
    div.appendChild(iconDiv);
    summary.appendChild(div);

    const displayVertical = document.createElement('div');
    displayVertical.classList.add('facets__display-vertical');
    const headerVertical = document.createElement('div');
    headerVertical.classList.add('facets__header-vertical');
    const selectedSpan = document.createElement('span');
    selectedSpan.classList.add('facets__selected');
    selectedSpan.textContent = label;
    headerVertical.appendChild(selectedSpan);
    displayVertical.appendChild(headerVertical);

    return [summary, displayVertical];
  }
}

customElements.define("algolia-range-input", AlgoliaRangeInput);